<template>
  <div class="field-group" :class="state">
    <!-- LABEL -->
    <label v-if="label" class="field-label">
      {{ label }}
      <span v-if="required" class="required">*</span>
    </label>

    <!-- INPUT -->
    <div class="input-wrapper" :class="{ disabled }">
      <input
        type="text"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="displayValue"
        maxlength="10"
        @input="onInput"
        @blur="onBlur"
      />

      <!-- calendar icon -->
      <span class="icon" @click="openPicker">
        <slot name="icon">
          <!-- default calendar -->
          <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H3V0H5V2H13V0H15V2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V18C18 18.55 17.8042 19.0208 17.4125 19.4125C17.0208 19.8042 16.55 20 16 20H2ZM2 18H16V8H2V18Z" fill="#6D6F7A"/>
          </svg>
        </slot>
      </span>

      <!-- hidden date picker -->
      <input
        ref="picker"
        type="date"
        class="hidden-date"
        @change="onPick"
      />
    </div>

    <!-- MESSAGE -->
    <div v-if="showMessage" class="field-message">
      <span>{{ message }}</span>
    </div>
  </div>
</template>
<script>
export default {
  name: 'FieldDate',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: String,
    required: Boolean,
    placeholder: {
      type: String,
      default: 'DD/MM/YYYY'
    },
    state: {
      type: String,
      default: 'default'
    },
    message: String,
    disabled: Boolean
  },
  data () {
    return {
      displayValue: this.modelValue
    }
  },
  watch: {
    modelValue (val) {
      this.displayValue = val
    }
  },
  computed: {
    showMessage () {
      return !!this.message && this.state !== 'default'
    }
  },
  methods: {
    /* ========= INPUT MANUAL ========= */
    onInput (e) {
      let val = e.target.value.replace(/\D/g, '') // angka saja
      if (val.length > 8) val = val.slice(0, 8)

      if (val.length >= 5) {
        val = `${val.slice(0, 2)}/${val.slice(2, 4)}/${val.slice(4)}`
      } else if (val.length >= 3) {
        val = `${val.slice(0, 2)}/${val.slice(2)}`
      }

      this.displayValue = val
    },

    onBlur () {
      if (this.isValidDate(this.displayValue)) {
        this.$emit('update:modelValue', this.displayValue)
      } else {
        const today = this.formatDate(new Date())
        this.displayValue = today
        this.$emit('update:modelValue', today)
      }
    },

    /* ========= DATE PICKER ========= */
    openPicker () {
      if (!this.disabled) {
        this.$refs.picker.showPicker?.()
      }
    },

    onPick (e) {
      const date = new Date(e.target.value)
      const formatted = this.formatDate(date)
      this.displayValue = formatted
      this.$emit('update:modelValue', formatted)
    },

    /* ========= UTIL ========= */
    isValidDate (val) {
      if (!/^(\d{2})\/(\d{2})\/(\d{4})$/.test(val)) return false

      const [d, m, y] = val.split('/').map(Number)
      const date = new Date(y, m - 1, d)

      return (
        date.getFullYear() === y &&
        date.getMonth() === m - 1 &&
        date.getDate() === d
      )
    },

    formatDate (date) {
      const dd = String(date.getDate()).padStart(2, '0')
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const yyyy = date.getFullYear()
      return `${dd}/${mm}/${yyyy}`
    }
  }
}
</script>

<style scoped>
.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

/* LABEL */
.field-label {
  font-size: 14px;
  font-weight: 600;
  color: #3c3f44;
}

.required {
  color: #f44336;
  margin-left: 2px;
}

/* INPUT WRAPPER */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper input {
  width: 100%;
  padding: 16px 40px 16px 14px;
  border-radius: 8px;
  border: 1.5px solid #d0d5dd;
  font-size: 14px;
  outline: none;
  background: #fff;
  transition: border-color 0.2s;
}

/* ICON */
.icon {
  position: absolute;
  right: 16px;
  display: flex;
  align-items: center;
}

/* MESSAGE */
.field-message {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}

/* STATES */
.field-group.default input {
  border-color: #d0d5dd;
}

.field-group.default input:focus {
  border-color: #7b61ff;
}

/* ERROR */
.field-group.error input {
  border-color: #f44336;
}
.field-group.error .field-message {
  color: #f44336;
}

/* WARNING */
.field-group.warning input {
  border-color: #f5a544;
}
.field-group.warning .field-message {
  color: #f5a544;
}

/* SUCCESS */
.field-group.success input {
  border-color: #2e7d32;
}
.field-group.success .field-message {
  color: #2e7d32;
}

/* DISABLED */
.input-wrapper.disabled input {
  background: #f2f4f7;
  border-color: #e4e7ec;
  color: #98a2b3;
}
.input-wrapper.disabled input::placeholder {
  color: #98a2b3;
}

.field-message.default {
  color: #667085;
}

.field-message.error {
  color: #f44336;
}

.field-message.warning {
  color: #f5a544;
}

.field-message.success {
  color: #2e7d32;
}
.hidden-date {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
</style>
